import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  TokenInfo as TokenInfoBase,
  TokenList as TokenListData,
} from '@uniswap/token-lists';
import axios from 'axios';
import {useDebouncedCallback} from 'use-debounce';
import {assetUrl} from '../utils';

const lists: Record<number, string[]> = {
  [42161]: [
    // "https://tokenlist.arbitrum.io/ArbTokenLists/arbed_arb_whitelist_era.json",
    process.env.NEXT_PUBLIC_API_URL + '/tokens/token-list?chainId=42161',
    'https://raw.githubusercontent.com/KEI-finance/default-token-list/master/arbitrum.json',
  ],
  [5]: [
    'https://raw.githubusercontent.com/KEI-finance/default-token-list/master/goerli.json',
  ],
  [11155111]: [
    // TODO fix me
    'https://raw.githubusercontent.com/KEI-finance/default-token-list/master/sepolia.json',
  ],
  [1]: ['https://tokens.coingecko.com/uniswap/all.json'],
};

export const TokenListContext = createContext<TokenList>(null!);

export interface TokenList {
  lists?: TokenListData[];
  tokens: TokenInfo[];
  tokenMap: Record<string, TokenInfo>;
  isLoading: boolean;
}

export interface TokenColor {
  vibrant?: string;
  muted?: string;
  darkVibrant?: string;
  lightVibrant?: string;
  darkMuted?: string;
  lightMuted?: string;
}

export interface TokenInfo extends TokenInfoBase {
  color?: TokenColor;
}

export const useTokenList = () => {
  const tokenList = useContext(TokenListContext);
  const [query, _setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filteredTokens, setFilteredTokens] = useState<TokenInfo[] | undefined>(
    undefined
  );

  if (!tokenList) {
    throw new Error('missing token list provider.');
  }

  const debounceFilter = useDebouncedCallback((query: string) => {
    if (!query) {
      setFilteredTokens(undefined);
    } else {
      query = query.toLowerCase();

      if (!tokenList.tokens) {
        throw new Error('please wait for token list to load');
      }

      setFilteredTokens(
        tokenList.tokens.filter(token => {
          return (
            token.name.toLowerCase().includes(query) ||
            token.address.toLowerCase().includes(query) ||
            token.symbol.toLowerCase().includes(query)
          );
        })
      );
    }
    setIsLoading(false);
  });

  const setQuery = useCallback((query: string) => {
    _setQuery(query);
    setIsLoading(true);
    debounceFilter(query);
  }, []);

  return {
    ...tokenList,
    setQuery,
    filteredTokens: filteredTokens || tokenList.tokens,
    isLoading: isLoading || tokenList.isLoading,
    query,
  };
};

export interface TokenListProviderProps extends PropsWithChildren {
  chainId: number;
}

export const NativeTokens: Record<number, TokenInfo> = {
  [5]: {
    address: '0x'.padEnd(42, '0'),
    symbol: 'ETH',
    name: 'Ethereum',
    chainId: 5,
    decimals: 18,
    logoURI: assetUrl('payment/eth-icon-dark.svg'),
  },
  [42161]: {
    address: '0x'.padEnd(42, 'e'),
    symbol: 'ETH',
    name: 'Ethereum',
    chainId: 42161,
    decimals: 18,
    logoURI: assetUrl('payment/eth-icon-dark.svg'),
  },
  [11155111]: {
    address: '0x'.padEnd(42, '0'),
    symbol: 'ETH',
    name: 'Ethereum',
    chainId: 11155111,
    decimals: 18,
    logoURI: assetUrl('payment/eth-icon-dark.svg'),
  },
};

export const TokenListProvider = ({
  children,
  chainId,
}: TokenListProviderProps) => {
  const [tokenLists, setTokenLists] = useState<TokenListData[] | undefined>(
    undefined
  );

  useEffect(() => {
    const list = lists[chainId];

    if (!list) {
      throw new Error('missing token list for chain id ' + chainId);
    }

    Promise.all(
      list.map(async url => {
        const result = await axios.get<TokenListData>(url);
        return result.data;
      })
    ).then(lists => setTokenLists(lists));
  }, []);

  const {tokens, tokenMap} = useMemo(() => {
    if (!tokenLists) {
      return {tokenMap: {}, tokens: []};
    }

    const tokenMap: Record<string, TokenInfo> = {};
    const tokens: TokenInfo[] = [];

    [
      NativeTokens[chainId],
      ...tokenLists.flatMap(data => {
        return data.tokens;
      }),
    ].forEach(token => {
      if (token.chainId !== chainId || tokenMap[token.address.toLowerCase()])
        return;
      tokenMap[token.symbol.toLowerCase()] = token;
      tokenMap[token.address.toLowerCase()] = token;
      tokens.push(token);
    });

    return {tokens, tokenMap};
  }, [tokenLists, chainId]);

  return (
    <TokenListContext.Provider
      value={{
        lists: tokenLists,
        tokens,
        tokenMap,
        isLoading: !tokenLists,
      }}
    >
      {children}
    </TokenListContext.Provider>
  );
};
